import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Modal,
  Badge,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { fires } from "../firebase";
import { Helmet } from "react-helmet";
import {
  FaChair,
  FaPlus,
  FaTrash,
  FaEdit,
  FaSave,
  FaTimes,
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaMap,
  FaUsers,
  FaCog,
  FaInfoCircle,
  FaCalendar,
  FaCalendarAlt,
  FaBug,
} from "react-icons/fa";
import "firebase/firestore";
import firebase from "firebase";

export default function SeatSelection() {
  const { eventId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [seatMap, setSeatMap] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showAddSeatsModal, setShowAddSeatsModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showBulkTypeModal, setShowBulkTypeModal] = useState(false);
  const [editingSeat, setEditingSeat] = useState(null);
  const [viewMode, setViewMode] = useState("edit"); // "edit" or "view"
  const [selectionMode, setSelectionMode] = useState(false); // Enable multi-select mode
  const [selectedSeatIds, setSelectedSeatIds] = useState(new Set()); // Selected seat IDs
  const [debugInfo, setDebugInfo] = useState("");
  const [currentNamingMode, setCurrentNamingMode] = useState('default');
  
  // Form refs
  const rowsRef = useRef();
  const columnsRef = useRef();
  const seatNameRef = useRef();
  const seatTypeRef = useRef();
  const seatPriceRef = useRef();
  const seatStatusRef = useRef();
  const namingModeRef = useRef();
  const rowPrefixRef = useRef();
  const useRowNumberRef = useRef();
  const addRowsRef = useRef();
  const addColumnsRef = useRef();
  const addNamingModeRef = useRef();
  const addRowPrefixRef = useRef();
  const addUseRowNumberRef = useRef();
  const [addSeatsNamingMode, setAddSeatsNamingMode] = useState('default');
  const initialStatusRef = useRef();
  const addInitialStatusRef = useRef();
  const bulkStatusRef = useRef();
  const bulkSeatTypeRef = useRef();
  const quickSelectInputRef = useRef();

  // Seat configuration
  const [seatConfig, setSeatConfig] = useState({
    rows: 10,
    columns: 12,
    seatTypes: [],
    namingMode: 'default', // 'default' or 'custom'
    rowPrefix: 'G1', // Custom row prefix (e.g., 'G1', 'A', etc.)
    useRowNumber: true, // Whether to include row number in custom naming
    columnPadding: 2 // Number of digits for column padding (e.g., 2 = 01, 02, etc.)
  });

  // Bulk assignment state
  const [bulkAssignment, setBulkAssignment] = useState({
    startRow: 0,
    endRow: 0,
    startCol: 0,
    endCol: 0,
    seatType: "default"
  });

  // Shared seating state
  const [showSharedSeatingModal, setShowSharedSeatingModal] = useState(false);
  const [sharedSeatingConfig, setSharedSeatingConfig] = useState({
    sourceEventId: "",
    copySeatNames: true,
    generateNewIds: true,
    copySeatTypes: true,
    copySeatStatus: false
  });
  const [availableEvents, setAvailableEvents] = useState([]);

  // Multi-day seat sets state
  const [showMultiDayModal, setShowMultiDayModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState("");
  const [seatSets, setSeatSets] = useState({
    "aug2": [],
    "aug3": []
  });
  const [currentSet, setCurrentSet] = useState("aug2");

  // Check access and load event data
  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    loadEventData();
  }, [currentUser, eventId, navigate]);

  // Load existing seat map on component mount
  // useEffect(() => {
  //   if (eventId) {
  //     loadMultiDateSeatMaps();
  //   }
  // }, [eventId]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      
      // Load event data
      const eventDoc = await fires.collection("events").doc(eventId).get();
      if (!eventDoc.exists) {
        setError("Event not found");
        return;
      }
      
      const eventInfo = eventDoc.data();
      setEventData(eventInfo);
      
      // Extract seat types from event prices
      console.log("Event prices:", eventInfo.prices);
      const seatTypes = eventInfo.prices ? Object.values(eventInfo.prices).map(price => {
        console.log("Processing price:", price);
        console.log("Price ID field:", price.id);
        console.log("Price object keys:", Object.keys(price));
        return {
          name: price.priceDescription || "Standard",
          color: getSeatTypeColor(price.priceDescription || "Standard"),
          price: price.price || 0,
          id: price.id
        };
      }) : [
        { name: "Standard", color: "#667eea", price: 0, id: "default" }
      ];
      
      console.log("Extracted seat types:", seatTypes);
      
      setDebugInfo(JSON.stringify({
        eventPrices: eventInfo.prices,
        extractedSeatTypes: seatTypes,
        seatTypeCount: seatTypes.length
      }, null, 2));
      
      setSeatConfig(prev => ({
        ...prev,
        seatTypes: seatTypes
      }));
      
      // Load existing seat map
      const seatMapRef = await fires.collection("events").doc(eventId).collection("seats").get();
      
      console.log("Loading seats - found", seatMapRef.docs.length, "seat documents");
      
      if (seatMapRef.docs.length > 0) {
        // Convert existing seats to our format
        const existingSeats = [];
        let maxRow = 0;
        let maxCol = 0;
        
        seatMapRef.docs.forEach((doc) => {
          const seatData = doc.data();
          console.log("Processing seat:", doc.id, seatData);
          
          // Handle both x/y and row/column formats, and handle missing coordinates
          const row = seatData.x !== undefined ? seatData.x : (seatData.row !== undefined ? seatData.row : 0);
          const col = seatData.y !== undefined ? seatData.y : (seatData.column !== undefined ? seatData.column : 0);
          
          // ALWAYS preserve the saved name if it exists - never regenerate for existing seats
          // Only generate name/ID if the seat doesn't have one saved (for backward compatibility)
          const { name: generatedName, id: generatedId } = generateSeatNameAndId(row, col);
          const seat = {
            id: doc.id || generatedId,
            // CRITICAL: Always use saved name if it exists to preserve custom naming (G1-G10, V1-V10, etc.)
            name: seatData.name || generatedName,
            row: row,
            column: col,
            type: seatData.priceId || seatData.type || "default",
            priceId: seatData.priceId || seatData.type || "default",
            price: 0,
            status: seatData.status === "pending" ? "available" : 
                    seatData.status === "taken" ? "sold" : 
                    seatData.status === "reserved" ? "reserved" : 
                    seatData.status === "available" ? "available" : "available",
            isSelected: (seatData.ticketId || "") != ""
          };
          existingSeats.push(seat);
          
          if (row > maxRow) maxRow = row;
          if (col > maxCol) maxCol = col;
        });
        
        console.log("Processed", existingSeats.length, "seats, maxRow:", maxRow, "maxCol:", maxCol);
        setSeatMap(existingSeats);
        setSeatConfig(prev => ({
          ...prev,
          rows: maxRow + 1,
          columns: maxCol + 1
        }));
      } else {
        console.log("No seats found, creating default seat map");
        // Create default seat map with updated seat config
        const updatedSeatConfig = {
          rows: 10,
          columns: 12,
          seatTypes: seatTypes
        };
        setSeatConfig(updatedSeatConfig);
        generateDefaultSeatMapWithConfig(updatedSeatConfig);
      }

      // Load multi-date seat maps (only if seats were found, otherwise skip to avoid overwriting)
      if (seatMapRef.docs.length > 0) {
        await loadMultiDateSeatMaps();
      }
    } catch (error) {
      setError("Error loading event data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate seat name and ID
  // Supports both default (A01, A02) and custom (G1-01, G1-02) naming patterns
  const generateSeatNameAndId = (row, col, config = seatConfig) => {
    const colNumber = String(col + 1).padStart(config.columnPadding || 2, '0'); // 01, 02, 03, etc. (1-indexed, zero-padded)
    
    if (config.namingMode === 'custom' && config.rowPrefix) {
      // Custom naming: G01, G02, V01, V02, etc. or G1-01, G1-02 if useRowNumber is true
      const rowPrefix = config.rowPrefix.trim();
      const rowNum = config.useRowNumber ? String(row + 1) : '';
      // If row number is included, use format: G1-01, G1-02
      // If row number is not included, use format: G01, G02 (no dash)
      const seatName = rowNum ? `${rowPrefix}${rowNum}-${colNumber}` : `${rowPrefix}${colNumber}`;
      const seatId = `row-${rowPrefix.toLowerCase()}${rowNum ? rowNum : ''}-${colNumber}`;
      return { name: seatName, id: seatId };
    } else {
      // Default naming: A01, A02, B01, B02, etc.
      const rowLetterUpper = String.fromCharCode(65 + row); // A, B, C, etc. (uppercase)
      const rowLetterLower = String.fromCharCode(97 + row); // a, b, c, etc. (lowercase)
      const rowNumber = String(row + 1); // 1-indexed row number
      const seatName = `${rowLetterUpper}${colNumber}`; // A01, A02, etc.
      const seatId = `row-${rowLetterLower}${rowNumber}-${colNumber}`; // row-a1-01, row-a1-02, etc.
      return { name: seatName, id: seatId };
    }
  };

  const generateDefaultSeatMapWithConfig = (config, initialStatus = "available") => {
    console.log("Generating default seat map with config:", config);
    console.log("Rows:", config.rows, "Columns:", config.columns);
    console.log("Initial status:", initialStatus);
    
    const seats = [];
    const rows = config.rows;
    const columns = config.columns;
    const defaultSeatType = config.seatTypes.length > 0 ? config.seatTypes[0].id : "default";
    
    console.log("Default seat type:", defaultSeatType);
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const { name: seatName, id: seatId } = generateSeatNameAndId(row, col, config);
        
        seats.push({
          id: seatId,
          name: seatName,
          row: row,
          column: col,
          type: defaultSeatType,
          priceId: defaultSeatType,
          price: 0,
          status: initialStatus, // available, reserved, sold, disabled, pending
          isSelected: false
        });
      }
    }
    
    console.log("Generated seats:", seats);
    console.log("Generated seats length:", seats.length);
    
    setSeatMap(seats);
    updateBothDaySets(seats);
  };

  const updateBothDaySets = (seats) => {
    setSeatSets({
      aug2: seats.map(seat => ({ ...seat, day: 'aug2' })),
      aug3: seats.map(seat => ({ ...seat, day: 'aug3' }))
    });
    setSeatMap(seats);
  };

  const handleCreateSeatMap = () => {
    const rows = parseInt(rowsRef.current.value) || 10;
    const columns = parseInt(columnsRef.current.value) || 12;
    const namingMode = namingModeRef.current?.value || 'default';
    const rowPrefix = rowPrefixRef.current?.value || 'G1';
    const useRowNumber = useRowNumberRef.current?.checked !== false;
    
    // For default naming (A-Z), limit to 26 rows. For custom naming, allow more rows
    const maxRows = namingMode === 'custom' ? 1000 : 26;
    if (rows < 1 || rows > maxRows) {
      setError(`Rows must be between 1 and ${maxRows}${namingMode === 'custom' ? ' (custom naming allows more rows)' : ' (A-Z letters)'}`);
      setShowToast(true);
      return;
    }
    
    if (columns < 1 || columns > 100) {
      setError("Columns must be between 1 and 100");
      setShowToast(true);
      return;
    }
    
    if (namingMode === 'custom' && !rowPrefix.trim()) {
      setError("Row prefix is required for custom naming");
      setShowToast(true);
      return;
    }
    
    setSeatConfig(prev => ({
      ...prev,
      rows: rows,
      columns: columns,
      namingMode: namingMode,
      rowPrefix: rowPrefix.trim(),
      useRowNumber: useRowNumber,
      columnPadding: 2
    }));
    
    const initialStatus = initialStatusRef.current?.value || 'available';
    
    const updatedConfig = {
      rows: rows,
      columns: columns,
      seatTypes: seatConfig.seatTypes,
      namingMode: namingMode,
      rowPrefix: rowPrefix.trim(),
      useRowNumber: useRowNumber,
      columnPadding: 2
    };
    
    generateDefaultSeatMapWithConfig(updatedConfig, initialStatus);
    setShowCreateModal(false);
    setSuccess("Seat map created successfully!");
    setShowToast(true);
  };

  const handleAddSeats = () => {
    if (seatMap.length === 0) {
      setError("Please create a seat map first");
      setShowToast(true);
      return;
    }

    const rowsToAdd = parseInt(addRowsRef.current?.value) || 0;
    const columnsToAdd = parseInt(addColumnsRef.current?.value) || 0;
    const namingMode = addNamingModeRef.current?.value || seatConfig.namingMode || 'default';
    const rowPrefix = addRowPrefixRef.current?.value || seatConfig.rowPrefix || 'G1';
    const useRowNumber = addUseRowNumberRef.current?.checked !== false;
    
    if (rowsToAdd < 1) {
      setError("Please enter a valid number of rows to add (at least 1)");
      setShowToast(true);
      return;
    }
    
    if (columnsToAdd < 0) {
      setError("Columns to add cannot be negative");
      setShowToast(true);
      return;
    }

    if (namingMode === 'custom' && !rowPrefix.trim()) {
      setError("Row prefix is required for custom naming");
      setShowToast(true);
      return;
    }

    // Get current max columns to determine new column count
    const currentMaxCol = Math.max(...seatMap.map(s => s.column), -1) + 1;
    const newMaxColumns = Math.max(currentMaxCol, seatConfig.columns + columnsToAdd);
    
    // Create config for new seats (use custom naming if specified, otherwise use existing config)
    const newSeatsConfig = {
      ...seatConfig,
      namingMode: namingMode,
      rowPrefix: namingMode === 'custom' ? rowPrefix.trim() : seatConfig.rowPrefix,
      useRowNumber: namingMode === 'custom' ? useRowNumber : seatConfig.useRowNumber,
      columnPadding: 2
    };
    
    // Shift all existing seats down by rowsToAdd
    // CRITICAL: Preserve original names/IDs to avoid renumbering when adding seats with different prefixes
    // When adding V1-V10 after G1-G10, G seats must keep their original names
    const shiftedExistingSeats = seatMap.map(seat => {
      const newRow = seat.row + rowsToAdd;
      
      // Create a copy of the seat with updated row position but PRESERVED name and ID
      const preservedSeat = {
        ...seat, // Copy all properties
        row: newRow, // Only update row position
        // Explicitly preserve name and ID - DO NOT regenerate
        name: seat.name, // Keep original name (e.g., "G1", "G2", etc.)
        id: seat.id // Keep original ID
      };
      
      console.log(`Preserving seat: ${seat.name} (row ${seat.row} -> ${newRow})`);
      return preservedSeat;
    });

    // Get initial status for new seats
    const initialStatus = addInitialStatusRef.current?.value || 'available';
    
    // Generate new seats at the top (rows 0 to rowsToAdd-1) using the specified naming
    // For custom naming without row numbers, continue numbering sequentially across all rows
    const newSeats = [];
    const defaultSeatType = seatConfig.seatTypes.length > 0 ? seatConfig.seatTypes[0].id : "default";
    
    // If custom naming without row numbers, we need sequential numbering across all rows
    if (namingMode === 'custom' && !useRowNumber && rowPrefix.trim()) {
      let seatCounter = 1; // Start from 01
      const prefix = rowPrefix.trim();
      
      for (let row = 0; row < rowsToAdd; row++) {
        for (let col = 0; col < newMaxColumns; col++) {
          const colNumber = String(seatCounter).padStart(2, '0'); // 01, 02, 03, etc.
          const seatName = `${prefix}${colNumber}`; // V01, V02, V03...
          const seatId = `row-${prefix.toLowerCase()}-${colNumber}`;
          
          newSeats.push({
            id: seatId,
            name: seatName,
            row: row,
            column: col,
            type: defaultSeatType,
            priceId: defaultSeatType,
            price: 0,
            status: initialStatus,
            isSelected: false
          });
          
          seatCounter++;
        }
      }
    } else {
      // Default behavior: use row-based naming
      for (let row = 0; row < rowsToAdd; row++) {
        for (let col = 0; col < newMaxColumns; col++) {
          const { name: seatName, id: seatId } = generateSeatNameAndId(row, col, newSeatsConfig);
          
          newSeats.push({
            id: seatId,
            name: seatName,
            row: row,
            column: col,
            type: defaultSeatType,
            priceId: defaultSeatType,
            price: 0,
            status: initialStatus,
            isSelected: false
          });
        }
      }
    }

    // Combine new seats with shifted existing seats
    // Verify that existing seat names are preserved
    const existingSeatNames = seatMap.map(s => s.name);
    const preservedSeatNames = shiftedExistingSeats.map(s => s.name);
    const nameMatches = existingSeatNames.every((name, idx) => name === preservedSeatNames[idx]);
    
    if (!nameMatches) {
      console.error('WARNING: Seat names were changed during shift!');
      console.log('Original names:', existingSeatNames.slice(0, 10));
      console.log('Preserved names:', preservedSeatNames.slice(0, 10));
    }
    
    const updatedSeatMap = [...newSeats, ...shiftedExistingSeats];
    
    // Verify final seat map has correct names
    console.log('Final seat map - first 10 existing seats:', 
      updatedSeatMap.slice(newSeats.length, newSeats.length + 10).map(s => ({ name: s.name, row: s.row })));
    console.log('New seats:', newSeats.slice(0, 5).map(s => ({ name: s.name, row: s.row })));
    
    // Update seat config - ONLY update dimensions, preserve naming mode and prefix
    // This ensures existing seats (like G1-G10) keep their names when adding new seats (like V1-V10)
    const newMaxRow = Math.max(...updatedSeatMap.map(s => s.row), -1) + 1;
    
    // CRITICAL: Do NOT update namingMode, rowPrefix, or useRowNumber when adding seats
    // This prevents existing seats from being renumbered when a different prefix is used
    setSeatConfig(prev => {
      const updated = {
        ...prev,
        rows: newMaxRow,
        columns: newMaxColumns
        // Explicitly preserve naming settings to prevent renumbering
      };
      console.log('Updated seatConfig (preserving naming):', updated);
      return updated;
    });

    setSeatMap(updatedSeatMap);
    updateBothDaySets(updatedSeatMap);
    setShowAddSeatsModal(false);
    setSuccess(`Successfully added ${rowsToAdd} row(s)${columnsToAdd > 0 ? ` and ${columnsToAdd} column(s)` : ''} on top!`);
    setShowToast(true);
  };

  const handleEditSeat = (seat) => {
    setEditingSeat(seat);
    setShowEditModal(true);
  };

  const handleSeatClick = (seat, event) => {
    if (selectionMode) {
      // In selection mode, toggle seat selection
      event.stopPropagation();
      setSelectedSeatIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(seat.id)) {
          newSet.delete(seat.id);
        } else {
          newSet.add(seat.id);
        }
        return newSet;
      });
    } else {
      // Normal mode - open edit modal
      if (viewMode === "edit") {
        handleEditSeat(seat);
      }
    }
  };

  const handleBulkStatusChange = async () => {
    if (selectedSeatIds.size === 0) {
      setError("Please select at least one seat");
      setShowToast(true);
      return;
    }

    const newStatus = bulkStatusRef.current?.value || 'available';
    if (!newStatus) {
      setError("Please select a status");
      setShowToast(true);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      // Convert status to Firestore format
      const firestoreStatus = newStatus === "available" ? "pending" : 
                              newStatus === "sold" ? "taken" : 
                              newStatus === "reserved" ? "reserved" : "pending";

      // Update in Firestore using batch
      const batch = fires.batch();
      const seatsToUpdate = seatMap.filter(seat => selectedSeatIds.has(seat.id));
      
      seatsToUpdate.forEach(seat => {
        const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
        batch.update(seatRef, {
          status: firestoreStatus,
          timestamp: firebase.firestore.Timestamp.fromDate(new Date())
        });
      });

      await batch.commit();

      // Update local state
      const updatedSeatMap = seatMap.map(seat => 
        selectedSeatIds.has(seat.id) ? { ...seat, status: newStatus } : seat
      );
      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);

      // Clear selection and exit selection mode
      setSelectedSeatIds(new Set());
      setSelectionMode(false);
      setShowBulkStatusModal(false);
      setSuccess(`Successfully updated status for ${seatsToUpdate.length} seat(s) to ${newStatus}`);
      setShowToast(true);
    } catch (error) {
      console.error("Error updating seat statuses:", error);
      setError("Error updating seat statuses: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkTypeChange = async () => {
    if (selectedSeatIds.size === 0) {
      setError("Please select at least one seat");
      setShowToast(true);
      return;
    }

    const newSeatType = bulkSeatTypeRef.current?.value || seatConfig.seatTypes[0]?.id || "default";
    if (!newSeatType) {
      setError("Please select a seat type");
      setShowToast(true);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const selectedSeatType = seatConfig.seatTypes.find(type => type.id === newSeatType);
      const seatsToUpdate = seatMap.filter(seat => selectedSeatIds.has(seat.id));

      const batch = fires.batch();
      seatsToUpdate.forEach(seat => {
        const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
        batch.set(seatRef, {
          id: seat.id,
          name: seat.name,
          x: seat.row,
          y: seat.column,
          status: seat.status === "available" ? "pending" :
                  seat.status === "sold" ? "taken" :
                  seat.status === "reserved" ? "reserved" : "pending",
          priceId: newSeatType,
          timestamp: firebase.firestore.Timestamp.fromDate(new Date())
        }, { merge: true });
      });

      await batch.commit();

      const updatedSeatMap = seatMap.map(seat =>
        selectedSeatIds.has(seat.id)
          ? {
              ...seat,
              type: newSeatType,
              priceId: newSeatType,
              price: selectedSeatType ? selectedSeatType.price : 0
            }
          : seat
      );

      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);
      setSelectedSeatIds(new Set());
      setSelectionMode(false);
      setShowBulkTypeModal(false);
      setSuccess(`Successfully updated type for ${seatsToUpdate.length} seat(s) to ${getSeatTypeName(newSeatType)}`);
      setShowToast(true);
    } catch (error) {
      console.error("Error updating seat types:", error);
      setError("Error updating seat types: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const clearSelection = () => {
    setSelectedSeatIds(new Set());
    setSelectionMode(false);
  };

  const parseSeatRange = (rangeText) => {
    // Parse patterns like "T01-01 to T01-10" or "T25-01 to T25-06"
    // Also support "A01 to A07" (no dash format)
    // Also support "T01-01 to T01-10, T25-01 to T25-06" (comma-separated)
    const ranges = rangeText.split(',').map(r => r.trim()).filter(r => r);
    const selectedIds = new Set(selectedSeatIds);
    
    ranges.forEach(range => {
      // Match pattern: "ROW-COL1 to ROW-COL2" (e.g., "T01-01 to T01-10")
      const rangeMatch = range.match(/^([A-Z0-9]+)-(\d+)\s+to\s+([A-Z0-9]+)-(\d+)$/i);
      if (rangeMatch) {
        const [, rowPrefix1, colStart, rowPrefix2, colEnd] = rangeMatch;
        
        // Check if both row prefixes match (same row)
        if (rowPrefix1.toUpperCase() === rowPrefix2.toUpperCase()) {
          const rowPrefix = rowPrefix1.toUpperCase();
          const startCol = parseInt(colStart, 10);
          const endCol = parseInt(colEnd, 10);
          
          if (startCol > endCol) {
            console.warn(`Invalid range: start column ${startCol} > end column ${endCol}`);
            return;
          }
          
          // Find all seats matching this row and column range
          for (let col = startCol; col <= endCol; col++) {
            // Try multiple name formats
            const possibleNames = [
              `${rowPrefix}-${col.toString().padStart(2, '0')}`, // T01-01
              `${rowPrefix}-${col}`, // T01-1
              `${rowPrefix}${col.toString().padStart(2, '0')}`, // T0101 (no dash)
              `${rowPrefix}${col}`, // T011 (no dash, no padding)
            ];
            
            // Find seat by name (try all formats)
            const seat = seatMap.find(s => {
              const seatNameUpper = s.name.toUpperCase();
              return possibleNames.some(name => seatNameUpper === name.toUpperCase());
            });
            
            if (seat) {
              selectedIds.add(seat.id);
            }
          }
        } else {
          console.warn(`Row prefixes don't match: ${rowPrefix1} vs ${rowPrefix2}`);
        }
      } else {
        // Try to match pattern: "ROWCOL1 to ROWCOL2" (e.g., "A01 to A07" - no dash)
        // This regex matches: letters (row prefix) followed by digits (column)
        const noDashRangeMatch = range.match(/^([A-Z]+)(\d+)\s+to\s+([A-Z]+)(\d+)$/i);
        if (noDashRangeMatch) {
          const [, rowPrefix1, colStart, rowPrefix2, colEnd] = noDashRangeMatch;
          
          // Check if both row prefixes match (same row)
          if (rowPrefix1.toUpperCase() === rowPrefix2.toUpperCase()) {
            const rowPrefix = rowPrefix1.toUpperCase();
            const startCol = parseInt(colStart, 10);
            const endCol = parseInt(colEnd, 10);
            
            if (startCol > endCol) {
              console.warn(`Invalid range: start column ${startCol} > end column ${endCol}`);
              return;
            }
            
            // Determine padding based on the input format
            const startColStr = colStart.toString();
            const endColStr = colEnd.toString();
            const usePadding = startColStr.length > 1 || endColStr.length > 1;
            const paddingLength = Math.max(startColStr.length, endColStr.length);
            
            // Find all seats matching this row and column range
            for (let col = startCol; col <= endCol; col++) {
              // Try multiple name formats for no-dash naming
              const possibleNames = [
                usePadding ? `${rowPrefix}${col.toString().padStart(paddingLength, '0')}` : `${rowPrefix}${col}`, // A01, A02, A03 or A1, A2, A3
                `${rowPrefix}${col.toString().padStart(2, '0')}`, // A01, A02 (always try 2-digit padding)
                `${rowPrefix}${col}`, // A1, A2, A3 (no padding)
                `${rowPrefix}-${col.toString().padStart(2, '0')}`, // A-01, A-02
                `${rowPrefix}-${col}`, // A-1, A-2
              ];
              
              // Find seat by name (try all formats)
              const seat = seatMap.find(s => {
                const seatNameUpper = s.name.toUpperCase();
                return possibleNames.some(name => seatNameUpper === name.toUpperCase());
              });
              
              if (seat) {
                selectedIds.add(seat.id);
              }
            }
          } else {
            console.warn(`Row prefixes don't match: ${rowPrefix1} vs ${rowPrefix2}`);
          }
        } else {
          // Try to match single seat name like "T01-01" or "A01"
          const singleMatch = range.match(/^([A-Z0-9]+)-?(\d+)$/i);
          if (singleMatch) {
            const [, rowPrefix, col] = singleMatch;
            const possibleNames = [
              `${rowPrefix.toUpperCase()}-${col.padStart(2, '0')}`, // T01-01
              `${rowPrefix.toUpperCase()}-${col}`, // T01-1
              `${rowPrefix.toUpperCase()}${col.padStart(2, '0')}`, // T0101 or A01
              `${rowPrefix.toUpperCase()}${col}`, // T011 or A1
            ];
            
            const seat = seatMap.find(s => {
              const seatNameUpper = s.name.toUpperCase();
              return possibleNames.some(name => seatNameUpper === name.toUpperCase());
            });
            
            if (seat) {
              selectedIds.add(seat.id);
            }
          }
        }
      }
    });
    
    return selectedIds;
  };

  const handleQuickSelect = () => {
    const inputValue = quickSelectInputRef.current?.value || '';
    if (!inputValue.trim()) {
      setError("Please enter a seat range (e.g., T01-01 to T01-10 or A01 to A07)");
      setShowToast(true);
      return;
    }

    try {
      const newSelection = parseSeatRange(inputValue);
      const addedCount = newSelection.size - selectedSeatIds.size;
      
      if (addedCount === 0) {
        setError("No matching seats found for the specified range");
        setShowToast(true);
        return;
      }
      
      setSelectedSeatIds(newSelection);
      setSuccess(`Added ${addedCount} seat(s) to selection`);
      setShowToast(true);
      
      // Clear input
      if (quickSelectInputRef.current) {
        quickSelectInputRef.current.value = '';
      }
    } catch (error) {
      console.error("Error parsing seat range:", error);
      setError("Invalid seat range format. Use format: ROW-COL1 to ROW-COL2 (e.g., T01-01 to T01-10)");
      setShowToast(true);
    }
  };

  const handleBulkDeleteSeats = async () => {
    if (selectedSeatIds.size === 0) {
      setError("Please select at least one seat to delete");
      setShowToast(true);
      return;
    }

    // Confirm deletion
    const confirmMessage = `Are you sure you want to delete ${selectedSeatIds.size} seat(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      // Get seats to delete
      const seatsToDelete = seatMap.filter(seat => selectedSeatIds.has(seat.id));
      
      // Delete from Firestore using batch
      const batch = fires.batch();
      seatsToDelete.forEach(seat => {
        const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
        batch.delete(seatRef);
      });

      await batch.commit();

      // Update local state - remove deleted seats
      const updatedSeatMap = seatMap.filter(seat => !selectedSeatIds.has(seat.id));
      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);

      // Clear selection and exit selection mode
      const deletedCount = seatsToDelete.length;
      setSelectedSeatIds(new Set());
      setSelectionMode(false);
      setSuccess(`Successfully deleted ${deletedCount} seat(s)`);
      setShowToast(true);
    } catch (error) {
      console.error("Error deleting seats:", error);
      setError("Error deleting seats: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeat = async () => {
    if (!editingSeat) return;
    
    try {
      setSaving(true);
      
      // Ensure priceId is set to the seat type
      const updatedSeat = {
        ...editingSeat,
        priceId: editingSeat.type
      };
      
      // Save directly to Firestore
      const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(updatedSeat.id);
      await seatRef.set({
        id: updatedSeat.id,
        name: updatedSeat.name,
        x: updatedSeat.row,
        y: updatedSeat.column,
        status: updatedSeat.status === "available" ? "pending" : 
                updatedSeat.status === "sold" ? "taken" : 
                updatedSeat.status === "reserved" ? "reserved" : "pending",
        priceId: updatedSeat.type,
        timestamp: firebase.firestore.Timestamp.fromDate(new Date())
      }, { merge: true }); // Use merge to preserve other fields like day, ticketId, etc.
      
      // Update local state
      const updatedSeatMap = seatMap.map(seat => 
        seat.id === editingSeat.id ? updatedSeat : seat
      );
      
      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);
      setShowEditModal(false);
      setEditingSeat(null);
      setSuccess("Seat updated successfully!");
      setShowToast(true);
    } catch (error) {
      setError("Error updating seat: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeat = async (seatId) => {
    try {
      setSaving(true);
      
      // Delete from Firestore
      await fires.collection("events").doc(eventId).collection("seats").doc(seatId).delete();
      
      // Update local state
      const updatedSeatMap = seatMap.filter(seat => seat.id !== seatId);
      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);
      setSuccess("Seat deleted successfully!");
      setShowToast(true);
    } catch (error) {
      setError("Error deleting seat: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSeatStatus = async (seatId, newStatus) => {
    try {
      const seat = seatMap.find(s => s.id === seatId);
      if (!seat) return;
      
      // Update in Firestore
      const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seatId);
      const firestoreStatus = newStatus === "available" ? "pending" : 
                              newStatus === "sold" ? "taken" : 
                              newStatus === "reserved" ? "reserved" : "pending";
      
      await seatRef.update({
        status: firestoreStatus,
        timestamp: firebase.firestore.Timestamp.fromDate(new Date())
      });
      
      // Update local state
      const updatedSeatMap = seatMap.map(seat => 
        seat.id === seatId ? { ...seat, status: newStatus } : seat
      );
      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);
    } catch (error) {
      console.error("Error updating seat status:", error);
      setError("Error updating seat status: " + error.message);
      setShowToast(true);
    }
  };

  const handleBulkAssignSeatType = async () => {
    const { startRow, endRow, startCol, endCol, seatType } = bulkAssignment;
    
    console.log("Bulk assignment config:", bulkAssignment);
    console.log("Selected seat type ID:", seatType);
    console.log("Available seat types:", seatConfig.seatTypes);
    
    if (startRow > endRow || startCol > endCol) {
      setError("Invalid range: start must be less than or equal to end");
      setShowToast(true);
      return;
    }
    
    try {
      setSaving(true);
      
      // Find the selected seat type to get its price information
      const selectedSeatType = seatConfig.seatTypes.find(type => type.id === seatType);
      console.log("Found selected seat type:", selectedSeatType);
      
      // Update debug info
      setDebugInfo(prev => prev + "\n\nBulk Assignment Debug:\n" + JSON.stringify({
        selectedSeatType,
        seatTypeId: seatType,
        availableSeatTypes: seatConfig.seatTypes,
        seatTypeMapping: seatConfig.seatTypes.map(type => ({
          id: type.id,
          name: type.name,
          price: type.price
        }))
      }, null, 2));
      
      // Get seats to update
      const seatsToUpdate = seatMap.filter(seat => 
        seat.row >= startRow && seat.row <= endRow && 
        seat.column >= startCol && seat.column <= endCol
      );
      
      // Update in Firestore using batch
      const batch = fires.batch();
      seatsToUpdate.forEach(seat => {
        const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
        batch.update(seatRef, {
          priceId: seatType,
          timestamp: firebase.firestore.Timestamp.fromDate(new Date())
        });
      });
      
      await batch.commit();
      
      // Update local state
      const updatedSeatMap = seatMap.map(seat => {
        if (seat.row >= startRow && seat.row <= endRow && 
            seat.column >= startCol && seat.column <= endCol) {
          return { 
            ...seat, 
            type: seatType,
            priceId: seatType, // Ensure priceId is set to the seat type ID
            price: selectedSeatType ? selectedSeatType.price : 0
          };
        }
        return seat;
      });
      
      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);
      setShowBulkAssignModal(false);
      setSuccess(`Updated ${seatsToUpdate.length} seats to ${getSeatTypeName(seatType)}`);
      setShowToast(true);
    } catch (error) {
      setError("Error updating seats: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAssignPremium = async () => {
    // Assign Premium to rows A, B, C (rows 0, 1, 2)
    const premiumType = seatConfig.seatTypes.find(type => 
      type.name.toLowerCase().includes('premium')
    );
    
    if (!premiumType) {
      setError("Premium seat type not found");
      setShowToast(true);
      return;
    }
    
    try {
      setSaving(true);
      
      // Get seats to update
      const seatsToUpdate = seatMap.filter(seat => seat.row <= 2);
      
      // Update in Firestore using batch
      const batch = fires.batch();
      seatsToUpdate.forEach(seat => {
        const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
        batch.update(seatRef, {
          priceId: premiumType.id,
          timestamp: firebase.firestore.Timestamp.fromDate(new Date())
        });
      });
      
      await batch.commit();
      
      // Update local state
      const updatedSeatMap = seatMap.map(seat => {
        if (seat.row <= 2) { // A, B, C rows
          return { 
            ...seat, 
            type: premiumType.id,
            priceId: premiumType.id,
            price: premiumType.price
          };
        }
        return seat;
      });
      
      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);
      setSuccess("Assigned Premium seats to rows A, B, C");
      setShowToast(true);
    } catch (error) {
      setError("Error assigning premium seats: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAssignGeneral = async () => {
    // Assign General to remaining rows (rows 3+)
    const generalType = seatConfig.seatTypes.find(type => 
      type.name.toLowerCase().includes('general')
    );
    
    if (!generalType) {
      setError("General seat type not found");
      setShowToast(true);
      return;
    }
    
    try {
      setSaving(true);
      
      // Get seats to update
      const seatsToUpdate = seatMap.filter(seat => seat.row > 2);
      
      // Update in Firestore using batch
      const batch = fires.batch();
      seatsToUpdate.forEach(seat => {
        const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
        batch.update(seatRef, {
          priceId: generalType.id,
          timestamp: firebase.firestore.Timestamp.fromDate(new Date())
        });
      });
      
      await batch.commit();
      
      // Update local state
      const updatedSeatMap = seatMap.map(seat => {
        if (seat.row > 2) { // D and beyond
          return { 
            ...seat, 
            type: generalType.id,
            priceId: generalType.id,
            price: generalType.price
          };
        }
        return seat;
      });
      
      setSeatMap(updatedSeatMap);
      updateBothDaySets(updatedSeatMap);
      setSuccess("Assigned General seats to remaining rows");
      setShowToast(true);
    } catch (error) {
      setError("Error assigning general seats: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeatMap = async () => {
    try {
      setSaving(true);
      
      console.log("Current seat map:", seatMap);
      console.log("Seat map length:", seatMap.length);
      
      // Convert our seat format to the existing format
      const seatsToSave = seatMap.map(seat => ({
        id: seat.id,
        name: seat.name,
        x: seat.row,
        y: seat.column,
        status: seat.status === "available" ? "pending" : 
                seat.status === "sold" ? "taken" : 
                seat.status === "reserved" ? "reserved" : "pending",
        priceId: seat.type,
        timestamp: new Date()
      }));
      
      console.log("Seats to save:", seatsToSave);
      console.log("Seats to save length:", seatsToSave.length);
      
      // Save each seat to the subcollection
    //   const batch = fires.batch();
      
    //   seatsToSave.forEach(seat => {
    //     const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
    //     batch.set(seatRef, {
    //       id: seat.id,
    //       name: seat.name,
    //       x: seat.x,
    //       y: seat.y,
    //       status: seat.status,
    //       priceId: seat.priceId,
    //       timestamp: firebase.firestore.Timestamp.fromDate(
    //         new Date()
    //       )
    //     });
    //   });
      
    //   await batch.commit();
      
      setSuccess(`Seat map saved successfully! Saved ${seatsToSave.length} seats`);
      setShowToast(true);
    } catch (error) {
      setError("Error saving seat map: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const getSeatTypeColor = (seatTypeName) => {
    // Color mapping for different seat types
    const colorMap = {
      "Standard": "#667eea",
      "Premium": "#48bb78", 
      "VIP": "#ed8936",
      "Wheelchair": "#9f7aea",
      "Economy": "#38b2ac",
      "Business": "#805ad5",
      "First Class": "#d69e2e"
    };
    
    return colorMap[seatTypeName] || "#667eea";
  };

  const getSeatTypeName = (typeId) => {
    const seatType = seatConfig.seatTypes.find(type => type.id === typeId);
    return seatType ? seatType.name : "Standard";
  };

  const getSeatTypeColorById = (typeId) => {
    const seatType = seatConfig.seatTypes.find(type => type.id === typeId);
    return seatType ? seatType.color : "#667eea";
  };

  const getSeatStatusColor = (status) => {
    switch (status) {
      case "available": return "#48bb78"; // pending
      case "reserved": return "#ed8936"; // reserved
      case "sold": return "#e53e3e"; // taken
      case "disabled": return "#a0aec0";
      default: return "#48bb78";
    }
  };

  const getSeatStatusText = (status) => {
    switch (status) {
      case "available": return "Available";
      case "reserved": return "Reserved";
      case "sold": return "Booked";
      case "disabled": return "Disabled";
      default: return "Available";
    }
  };

  const loadAvailableEvents = async () => {
    try {
      const eventsSnapshot = await fires.collection("events").get();
      const events = eventsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(event => event.id !== eventId) // Exclude current event
        .filter(event => event.title) // Only events with titles
        .slice(0, 20); // Limit to 20 events for performance
      
      setAvailableEvents(events);
    } catch (error) {
      setError("Error loading events: " + error.message);
      setShowToast(true);
    }
  };

  const handleCopySeatLayout = async () => {
    try {
      if (!sharedSeatingConfig.sourceEventId) {
        setError("Please select a source event");
        setShowToast(true);
        return;
      }

      // Load seats from source event
      const sourceSeatsSnapshot = await fires.collection("events")
        .doc(sharedSeatingConfig.sourceEventId)
        .collection("seats")
        .get();

      if (sourceSeatsSnapshot.empty) {
        setError("No seats found in the source event");
        setShowToast(true);
        return;
      }

      const sourceSeats = sourceSeatsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Convert to our format
      const newSeatMap = sourceSeats.map(seat => {
        const { name: generatedName, id: generatedId } = generateSeatNameAndId(seat.x, seat.y);
        const newSeat = {
          id: sharedSeatingConfig.generateNewIds ? generatedId : seat.id,
          name: sharedSeatingConfig.copySeatNames ? seat.name : generatedName,
          row: seat.x,
          column: seat.y,
          type: sharedSeatingConfig.copySeatTypes ? (seat.priceId || "default") : "default",
          priceId: sharedSeatingConfig.copySeatTypes ? (seat.priceId || "default") : "default",
          price: 0,
          status: sharedSeatingConfig.copySeatStatus ? 
            (seat.status === "pending" ? "available" : 
             seat.status === "taken" ? "sold" : 
             seat.status === "reserved" ? "reserved" : "available") : "available",
          isSelected: false
        };
        return newSeat;
      });

      // Update seat config based on source
      const maxRow = Math.max(...sourceSeats.map(s => s.x));
      const maxCol = Math.max(...sourceSeats.map(s => s.y));

      setSeatMap(newSeatMap);
      setSeatConfig(prev => ({
        ...prev,
        rows: maxRow + 1,
        columns: maxCol + 1
      }));

      setShowSharedSeatingModal(false);
      setSuccess(`Copied ${newSeatMap.length} seats from source event`);
      setShowToast(true);
    } catch (error) {
      setError("Error copying seat layout: " + error.message);
      setShowToast(true);
    }
  };

  const loadMultiDateSeatMaps = async () => {
    try {
      // Load existing seat sets from the event
      const seatsSnapshot = await fires.collection("events")
        .doc(eventId)
        .collection("seats")
        .get();

      const aug2Seats = [];
      const aug3Seats = [];
      
      seatsSnapshot.docs.forEach(doc => {
        const seatData = doc.data();
        // Handle both x/y and row/column formats
        const row = seatData.x !== undefined ? seatData.x : (seatData.row !== undefined ? seatData.row : 0);
        const col = seatData.y !== undefined ? seatData.y : (seatData.column !== undefined ? seatData.column : 0);
        
        const { name: generatedName, id: generatedId } = generateSeatNameAndId(row, col);
        const seat = {
          id: doc.id || generatedId,
          name: seatData.name || generatedName,
          row: row,
          column: col,
          type: seatData.priceId || seatData.type || "default",
          priceId: seatData.priceId || seatData.type || "default",
          price: 0,
          status: seatData.status === "pending" ? "available" : 
                  seatData.status === "taken" ? "sold" : 
                  seatData.status === "reserved" ? "reserved" : 
                  seatData.status === "available" ? "available" : "available",
          isSelected: (seatData.ticketId || "") != "",
          day: seatData.day || "aug2" // Default to aug2 if not specified
        };
        
        if (seatData.day === "aug3" || doc.id.includes("aug3")) {
          aug3Seats.push(seat);
        } else {
          aug2Seats.push(seat);
        }
      });

      console.log("Multi-day seats loaded - aug2:", aug2Seats.length, "aug3:", aug3Seats.length);

      setSeatSets({
        "aug2": aug2Seats,
        "aug3": aug3Seats
      });
      
      // Set current set to aug2 by default
      setCurrentSet("aug2");
      // Only update seatMap if we have seats in the day sets, otherwise keep existing seatMap
      // This prevents overwriting seats that were already loaded
      if (aug2Seats.length > 0) {
        setSeatMap(aug2Seats);
      } else if (aug3Seats.length > 0) {
        setSeatMap(aug3Seats);
      }
      // If no multi-day seats found, don't overwrite the seatMap that was already set
    } catch (error) {
      console.log("Error loading multi-day seat sets:", error);
    }
  };

  const switchToDay = (day) => {
    setCurrentSet(day);
    if (seatSets[day] && seatSets[day].length > 0) {
      setSeatMap(seatSets[day]);
    } else {
      // Create new seat set for this day
      generateDaySpecificSeatMap(day);
    }
  };

  const generateDaySpecificSeatMap = (day) => {
    const newSeatMap = [];
    const rows = seatConfig.rows;
    const columns = seatConfig.columns;
    const defaultSeatType = seatConfig.seatTypes.length > 0 ? seatConfig.seatTypes[0].id : "default";
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const { name: seatName, id: seatId } = generateSeatNameAndId(row, col, seatConfig);
        
        newSeatMap.push({
          id: `${day}-${seatId}`,
          name: seatName,
          row: row,
          column: col,
          type: defaultSeatType,
          priceId: defaultSeatType,
          price: 0,
          status: "available",
          isSelected: false,
          day: day
        });
      }
    }

    // Update seat sets state
    const updatedSeatSets = { ...seatSets, [day]: newSeatMap };
    setSeatSets(updatedSeatSets);
    setSeatMap(newSeatMap);
  };

  const createMultiDaySeatSets = async () => {
    try {
      if (!selectedDay) {
        setError("Please select a day");
        setShowToast(true);
        return;
      }

      // Generate seat map for the selected day
      generateDaySpecificSeatMap(selectedDay);
      
      setShowMultiDayModal(false);
      setSelectedDay("");

      setSuccess(`Created seat set for ${selectedDay === "aug2" ? "Aug 2" : "Aug 3"}`);
      setShowToast(true);
    } catch (error) {
      setError("Error creating multi-day seat sets: " + error.message);
      setShowToast(true);
    }
  };

  const createBothDaySets = async () => {
    try {
      // Generate seat maps for both days
      generateDaySpecificSeatMap("aug2");
      generateDaySpecificSeatMap("aug3");
      
      setShowMultiDayModal(false);
      setSelectedDay("");

      setSuccess("Created seat sets for both Aug 2 and Aug 3");
      setShowToast(true);
    } catch (error) {
      setError("Error creating both day sets: " + error.message);
      setShowToast(true);
    }
  };

  const saveMultiDaySeatSets = async () => {
    try {
      setSaving(true);
      
      console.log("Saving multi-day seat sets:", seatSets);
      console.log("Seat sets keys:", Object.keys(seatSets));
      
      // Save all seat sets to the same collection with day identifiers
      const batch = fires.batch();
      
      Object.entries(seatSets).forEach(([day, seats]) => {
        console.log(`Processing day ${day} with ${seats.length} seats`);
        seats.forEach(seat => {
          const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
          
          batch.set(seatRef, {
            id: seat.id,
            name: seat.name,
            x: seat.row,
            y: seat.column,
            status: seat.status === "available" ? "pending" : 
                    seat.status === "sold" ? "taken" : 
                    seat.status === "reserved" ? "reserved" : "pending",
            priceId: seat.type,
            timestamp: new Date(),
            day: day
          });
        });
      });
      
      await batch.commit();
      
      const totalSeats = Object.values(seatSets).reduce((sum, seats) => sum + seats.length, 0);
      console.log("Total seats saved:", totalSeats);
      setSuccess(`Saved ${totalSeats} seats across ${Object.keys(seatSets).length} day sets`);
      setShowToast(true);
    } catch (error) {
      setError("Error saving multi-day seat sets: " + error.message);
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const createDefaultSeatMap = () => {
    const defaultConfig = {
      rows: 10,
      columns: 12,
      seatTypes: seatConfig.seatTypes
    };
    generateDefaultSeatMapWithConfig(defaultConfig);
    setSuccess("Default seat map created with 10 rows × 12 columns");
    setShowToast(true);
  };

  useEffect(() => {
    if (seatSets.aug2 && seatSets.aug2.length > 0) {
      setSeatMap(seatSets.aug2);
    }
  }, [seatSets]);

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary" size="lg">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading seat map...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Seat Selection - {eventData?.title || "Event"}</title>
        <meta name="description" content="Manage seat map and seating arrangements" />
      </Helmet>

      <div className="min-vh-100" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <Container fluid className="py-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h2 mb-2 text-dark fw-bold" style={{
                background: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Seat Selection
              </h1>
              <p className="text-muted mb-0 fw-medium">
                {eventData?.title || "Event"} - Manage seating arrangements
              </p>
            </div>
            <div className="d-flex gap-3">
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate(`/admin/tourism-event/${eventId}`)}
                className="d-flex align-items-center gap-2 fw-medium"
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#cbd5e0';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
              >
                <FaArrowLeft /> Back to Event
              </Button>
            </div>
          </div>

          {/* Toast Notifications */}
          <ToastContainer position="top-end" className="p-3">
            <Toast 
              show={showToast} 
              onClose={() => setShowToast(false)}
              delay={5000}
              autohide
              bg={success ? "success" : "danger"}
              style={{
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                border: 'none'
              }}
            >
              <Toast.Header style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                borderRadius: '12px 12px 0 0'
              }}>
                <strong className="me-auto fw-semibold">
                  {success ? "Success" : "Error"}
                </strong>
              </Toast.Header>
              <Toast.Body className={success ? "text-white" : "text-white"}>
                {success || error}
              </Toast.Body>
            </Toast>
          </ToastContainer>

          {/* Main Content */}
          <Row>
            {/* Seat Map */}
            <Col lg={8}>
              <Card className="border-0" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-3">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)'
                      }}>
                        <FaChair style={{ color: 'white', fontSize: '20px' }} />
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold text-dark">Seat Map</h5>
                        <p className="text-muted mb-0 small">
                          {seatConfig.rows * seatConfig.columns} seats • {seatConfig.rows} rows × {seatConfig.columns} columns
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      {/* Day Selector */}
                      {/* Removed day selector and Add Day Set button */}
                    </div>
                  </div>

                  {/* Seat Grid */}
                  <div className="seat-map-container" style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    maxHeight: '70vh',
                    overflow: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 #f1f5f9'
                  }}>
                    <style>
                      {`
                        .seat-map-container::-webkit-scrollbar {
                          width: 8px;
                          height: 8px;
                        }
                        .seat-map-container::-webkit-scrollbar-track {
                          background: #f1f5f9;
                          border-radius: 4px;
                        }
                        .seat-map-container::-webkit-scrollbar-thumb {
                          background: #cbd5e0;
                          border-radius: 4px;
                        }
                        .seat-map-container::-webkit-scrollbar-thumb:hover {
                          background: #a0aec0;
                        }
                        .seat-map-container::-webkit-scrollbar-corner {
                          background: #f1f5f9;
                        }
                      `}
                    </style>
                    <div className="text-center mb-4">
                      <h6 className="fw-bold text-muted">STAGE</h6>
                      <div style={{
                        width: '60%',
                        height: '4px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        margin: '0 auto',
                        borderRadius: '2px'
                      }}></div>
                    </div>
                    
                    <div className="seat-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${seatConfig.columns}, 1fr)`,
                      gap: '10px',
                      maxWidth: '100%',
                      margin: '0 auto',
                      minWidth: `${seatConfig.columns * 80}px`
                    }}>
                      {seatMap.length === 0 ? (
                        <div className="text-center py-5" style={{ gridColumn: '1 / -1' }}>
                          <div style={{
                            width: '80px',
                            height: '80px',
                            background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                          }}>
                            <FaChair style={{ color: '#718096', fontSize: '28px' }} />
                          </div>
                          <h6 className="fw-semibold text-muted mb-2">No Seats Created</h6>
                          <p className="text-muted mb-3">Create a seat map to get started</p>
                          <Button
                            variant="primary"
                            onClick={createDefaultSeatMap}
                            className="fw-medium"
                            style={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '12px 24px'
                            }}
                          >
                            <FaPlus className="me-2" />
                            Create Default Seat Map
                          </Button>
                        </div>
                      ) : (
                        seatMap.map((seat) => (
                          <Button
                            key={seat.id}
                            variant="outline-secondary"
                            size="sm"
                            className="seat-button fw-medium"
                            onMouseEnter={(e) => {
                              if (viewMode === "edit" && seat.status !== "disabled") {
                                e.target.style.transform = 'scale(1.05)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (viewMode === "edit") {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                              }
                            }}
                            onClick={(e) => handleSeatClick(seat, e)}
                            disabled={seat.status === "disabled"}
                            style={{
                              ...(seat.status === "disabled" ? {} : {
                                width: '80px',
                                height: '80px',
                                minWidth: '80px',
                                minHeight: '80px',
                                maxWidth: '80px',
                                maxHeight: '80px',
                                borderRadius: '12px',
                                border: `2px solid ${selectionMode && selectedSeatIds.has(seat.id) ? '#667eea' : getSeatTypeColorById(seat.type)}`,
                                backgroundColor: selectionMode && selectedSeatIds.has(seat.id) ? '#e6f0ff' : (seat.status === "disabled" ? "#f7fafc" : "#ffffff"),
                                color: seat.status === "disabled" ? "#a0aec0" : "#2d3748",
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: selectionMode && selectedSeatIds.has(seat.id) ? '0 4px 12px rgba(102, 126, 234, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.05)',
                                position: 'relative',
                                fontSize: '1rem',
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                              })
                            }}
                          >
                            <div className="d-flex flex-column align-items-center w-100 h-100" style={{width: '100%', height: '100%', justifyContent: 'center'}}>
                              <span className="fw-bold" style={{
                                fontSize: '1.2em',
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                                lineHeight: 1.2,
                              }}>{seat.name}</span>
                              <small style={{
                                fontSize: '0.95em',
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                                lineHeight: 1.15,
                              }}>{getSeatTypeName(seat.type)}</small>
                              <small style={{
                                fontSize: '0.8em',
                                color: '#718096',
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                                lineHeight: 1.1,
                              }}>{getSeatStatusText(seat.status)}</small>
                            </div>
                            {seat.status !== "available" && (
                              <Badge 
                                style={{
                                  position: 'absolute',
                                  top: '-4px',
                                  right: '-4px',
                                  background: getSeatStatusColor(seat.status),
                                  border: '2px solid white',
                                  fontSize: '0.6rem'
                                }}
                              >
                                {seat.status === "reserved" ? "R" : seat.status === "sold" ? "B" : "D"}
                              </Badge>
                            )}
                          </Button>
                        ))
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Controls Panel */}
            <Col lg={4}>
              <div className="d-flex flex-column gap-4">
                {/* Create Seat Map */}
                <Card className="border-0" style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FaPlus style={{ color: 'white', fontSize: '14px' }} />
                      </div>
                      <h6 className="mb-0 fw-semibold">Create Seat Map</h6>
                    </div>
                    
                    <Form>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="text-muted small fw-medium">
                              Rows
                              {(currentNamingMode === 'custom' || seatConfig.namingMode === 'custom') && (
                                <span className="text-muted small ms-1">(up to 1000 with custom naming)</span>
                              )}
                            </Form.Label>
                            <Form.Control
                              type="number"
                              ref={rowsRef}
                              defaultValue={seatConfig.rows}
                              min="1"
                              max={currentNamingMode === 'custom' || seatConfig.namingMode === 'custom' ? "1000" : "26"}
                              style={{
                                height: '48px',
                                fontSize: '0.95rem',
                                fontWeight: '400',
                                color: '#2d3748',
                                backgroundColor: '#f7fafc',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                              }}
                            />
                            <Form.Text className="text-muted small">
                              {currentNamingMode === 'custom' || seatConfig.namingMode === 'custom' 
                                ? 'Custom naming allows up to 1000 rows' 
                                : 'Default naming (A-Z) allows up to 26 rows'}
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="text-muted small fw-medium">Columns</Form.Label>
                            <Form.Control
                              type="number"
                              ref={columnsRef}
                              defaultValue={seatConfig.columns}
                              min="1"
                              max="100"
                              style={{
                                height: '48px',
                                fontSize: '0.95rem',
                                fontWeight: '400',
                                color: '#2d3748',
                                backgroundColor: '#f7fafc',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                              }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      {/* Naming Configuration */}
                      <Form.Group className="mb-3">
                        <Form.Label className="text-muted small fw-medium">Seat Naming Mode</Form.Label>
                        <Form.Select
                          ref={namingModeRef}
                          defaultValue={seatConfig.namingMode || 'default'}
                          onChange={(e) => setCurrentNamingMode(e.target.value)}
                          style={{
                            height: '48px',
                            fontSize: '0.95rem',
                            fontWeight: '400',
                            color: '#2d3748',
                            backgroundColor: '#f7fafc',
                            border: '2px solid #e2e8f0',
                            borderRadius: '10px',
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                          }}
                        >
                          <option value="default">Default (A01, A02, B01, B02...)</option>
                          <option value="custom">Custom (G1-01, G1-02, G1-03...)</option>
                        </Form.Select>
                        <Form.Text className="text-muted small">
                          Choose how seats will be named
                        </Form.Text>
                      </Form.Group>
                      
                      {(currentNamingMode === 'custom' || seatConfig.namingMode === 'custom') && (
                        <>
                          <Row>
                            <Col md={8}>
                              <Form.Group className="mb-3">
                                <Form.Label className="text-muted small fw-medium">Row Prefix</Form.Label>
                                <Form.Control
                                  type="text"
                                  ref={rowPrefixRef}
                                  defaultValue={seatConfig.rowPrefix || 'G1'}
                                  placeholder="e.g., G1, A, Section1"
                                  style={{
                                    height: '48px',
                                    fontSize: '0.95rem',
                                    fontWeight: '400',
                                    color: '#2d3748',
                                    backgroundColor: '#f7fafc',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '10px',
                                    transition: 'all 0.2s ease-in-out',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                  }}
                                />
                                <Form.Text className="text-muted small">
                                  Prefix for seat names (e.g., "G1" will create G1-01, G1-02, etc.)
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group className="mb-3">
                                <Form.Label className="text-muted small fw-medium">Options</Form.Label>
                                <div>
                                  <Form.Check
                                    type="checkbox"
                                    ref={useRowNumberRef}
                                    defaultChecked={seatConfig.useRowNumber !== false}
                                    label="Include row number"
                                    className="small"
                                  />
                                  <Form.Text className="text-muted small d-block mt-1">
                                    If checked: G1-01, G1-02, G2-01, G2-02... If unchecked: G1-01, G1-02... (same prefix for all rows)
                                  </Form.Text>
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                          )}
                          
                          {/* Initial Status Selector */}
                          <Form.Group className="mb-3">
                            <Form.Label className="text-muted small fw-medium">Initial Seat Status</Form.Label>
                            <Form.Select
                              ref={initialStatusRef}
                              defaultValue="available"
                              style={{
                                height: '48px',
                                fontSize: '0.95rem',
                                fontWeight: '400',
                                color: '#2d3748',
                                backgroundColor: '#f7fafc',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                              }}
                            >
                              <option value="available">Available</option>
                              <option value="pending">Pending</option>
                              <option value="reserved">Reserved</option>
                              <option value="disabled">Disabled</option>
                            </Form.Select>
                            <Form.Text className="text-muted small">
                              All new seats will be created with this initial status
                            </Form.Text>
                          </Form.Group>
                          
                          <Alert variant="info" className="mb-4" style={{
                        background: 'linear-gradient(135deg, #ebf8ff 0%, #bee3f8 100%)',
                        border: '1px solid rgba(59, 130, 246, 0.1)',
                        borderRadius: '12px'
                      }}>
                        <div className="d-flex align-items-center gap-2">
                          <FaInfoCircle style={{ color: '#3182ce' }} />
                          <strong>Seat Configuration:</strong>
                        </div>
                        <p className="mb-0 mt-2">
                          {currentNamingMode === 'custom' || seatConfig.namingMode === 'custom' ? (
                            <>
                              <strong>Custom Naming:</strong> Seats will be named using your custom prefix (e.g., G1-01, G1-02, G1-03...)
                              <br />
                              Seat IDs will use the format: row-{`{prefix}`}{`{row}`}-{`{column}`} (e.g., row-g1-01, row-g1-02...)
                            </>
                          ) : (
                            <>
                              <strong>Default Naming:</strong> Seats will be automatically named using the format: Row Letter + Column Number (e.g., A01, A02, B01, B02...)
                              <br />
                              Seat IDs will use the format: row-{`{section}`}{`{row}`}-{`{column}`} (e.g., row-a1-01, row-a1-02, row-b1-01...)
                            </>
                          )}
                          <br />
                          Seat types and pricing will be based on your event's pricing configuration.
                        </p>
                      </Alert>

                      <Button
                        variant="outline-primary"
                        onClick={() => setShowCreateModal(true)}
                        className="w-100 fw-medium mb-2"
                        style={{
                          height: '48px',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          color: '#4facfe',
                          backgroundColor: 'white',
                          border: '2px solid #4facfe',
                          borderRadius: '10px',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: '0 2px 8px rgba(79, 172, 254, 0.15)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#4facfe';
                          e.target.style.color = 'white';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#4facfe';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(79, 172, 254, 0.15)';
                        }}
                      >
                        <FaMap className="me-2" /> Create New Map
                      </Button>
                      
                      <Button
                        variant="outline-success"
                        onClick={() => setShowAddSeatsModal(true)}
                        className="w-100 fw-medium"
                        disabled={seatMap.length === 0}
                        style={{
                          height: '48px',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          color: seatMap.length === 0 ? '#9ca3af' : '#48bb78',
                          backgroundColor: 'white',
                          border: `2px solid ${seatMap.length === 0 ? '#e5e7eb' : '#48bb78'}`,
                          borderRadius: '10px',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: '0 2px 8px rgba(72, 187, 120, 0.15)',
                          cursor: seatMap.length === 0 ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (seatMap.length > 0) {
                            e.target.style.backgroundColor = '#48bb78';
                            e.target.style.color = 'white';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(72, 187, 120, 0.25)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (seatMap.length > 0) {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.color = '#48bb78';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(72, 187, 120, 0.15)';
                          }
                        }}
                      >
                        <FaPlus className="me-2" /> Add Seats on Top
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>

                {/* Seat Types */}
                <Card className="border-0" style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FaCog style={{ color: 'white', fontSize: '14px' }} />
                      </div>
                      <h6 className="mb-0 fw-semibold">Seat Types & Pricing</h6>
                    </div>
                    <p className="text-muted mb-0 small">
                      {seatConfig.seatTypes.length} type{seatConfig.seatTypes.length !== 1 ? 's' : ''} from event pricing
                    </p>
                    
                    <div className="space-y-2">
                      {seatConfig.seatTypes.map((type, index) => (
                        <div key={index} className="d-flex align-items-center justify-content-between p-2" style={{
                          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                          borderRadius: '8px',
                          border: '1px solid rgba(0, 0, 0, 0.05)'
                        }}>
                          <div className="d-flex align-items-center gap-2">
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: type.color,
                              borderRadius: '4px'
                            }}></div>
                            <span className="fw-medium">{type.name}</span>
                          </div>
                          <Badge bg="secondary" className="fw-medium">
                            ${type.price}
                          </Badge>
                        </div>
                      ))}
                      {seatConfig.seatTypes.length === 0 && (
                        <div className="text-center text-muted p-3">
                          <small>No seat types configured</small>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>

                {/* Statistics */}
                <Card className="border-0" style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FaUsers style={{ color: 'white', fontSize: '14px' }} />
                      </div>
                      <h6 className="mb-0 fw-semibold">Statistics</h6>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Total Seats:</span>
                        <Badge bg="primary" className="fw-medium">{seatMap.length}</Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Available:</span>
                        <Badge bg="success" className="fw-medium">
                          {seatMap.filter(s => s.status === "available").length}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Reserved:</span>
                        <Badge bg="warning" className="fw-medium">
                          {seatMap.filter(s => s.status === "reserved").length}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Booked:</span>
                        <Badge bg="danger" className="fw-medium">
                          {seatMap.filter(s => s.status === "sold").length}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Disabled:</span>
                        <Badge bg="secondary" className="fw-medium">
                          {seatMap.filter(s => s.status === "disabled").length}
                        </Badge>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* Quick Assignment */}
                <Card className="border-0" style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FaUsers style={{ color: 'white', fontSize: '14px' }} />
                      </div>
                      <h6 className="mb-0 fw-semibold">Quick Assignment</h6>
                    </div>
                    
                    <div className="space-y-3">
                      <Button
                        variant="outline-warning"
                        onClick={handleQuickAssignPremium}
                        className="w-100 fw-medium"
                        style={{
                          height: '40px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          color: '#ed8936',
                          backgroundColor: 'white',
                          border: '2px solid #ed8936',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: '0 2px 8px rgba(237, 137, 54, 0.15)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#ed8936';
                          e.target.style.color = 'white';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(237, 137, 54, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#ed8936';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(237, 137, 54, 0.15)';
                        }}
                      >
                        Assign Premium to Rows A-C
                      </Button>
                      
                      <Button
                        variant="outline-info"
                        onClick={handleQuickAssignGeneral}
                        className="w-100 fw-medium"
                        style={{
                          height: '40px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          color: '#4facfe',
                          backgroundColor: 'white',
                          border: '2px solid #4facfe',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: '0 2px 8px rgba(79, 172, 254, 0.15)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#4facfe';
                          e.target.style.color = 'white';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#4facfe';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(79, 172, 254, 0.15)';
                        }}
                      >
                        Assign General to Remaining
                      </Button>
                      
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowBulkAssignModal(true)}
                        className="w-100 fw-medium"
                        style={{
                          height: '40px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          color: '#718096',
                          backgroundColor: 'white',
                          border: '2px solid #718096',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: '0 2px 8px rgba(113, 128, 150, 0.15)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#718096';
                          e.target.style.color = 'white';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(113, 128, 150, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#718096';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(113, 128, 150, 0.15)';
                        }}
                      >
                        Custom Range Assignment
                      </Button>
                      
                      <Button
                        variant="outline-success"
                        onClick={() => {
                          loadAvailableEvents();
                          setShowSharedSeatingModal(true);
                        }}
                        className="w-100 fw-medium"
                        style={{
                          height: '40px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          color: '#48bb78',
                          backgroundColor: 'white',
                          border: '2px solid #48bb78',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: '0 2px 8px rgba(72, 187, 120, 0.15)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#48bb78';
                          e.target.style.color = 'white';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(72, 187, 120, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#48bb78';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(72, 187, 120, 0.15)';
                        }}
                      >
                        Copy from Another Event
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {/* Bulk Status Change */}
                <Card className="border-0" style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FaCheck style={{ color: 'white', fontSize: '14px' }} />
                      </div>
                      <h6 className="mb-0 fw-semibold">Quick Status Change</h6>
                    </div>
                    
                    {!selectionMode ? (
                      <div className="space-y-3">
                        <Button
                          variant="outline-primary"
                          onClick={() => setSelectionMode(true)}
                          className="w-100 fw-medium"
                          style={{
                            height: '40px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            color: '#667eea',
                            backgroundColor: 'white',
                            border: '2px solid #667eea',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#667eea';
                            e.target.style.color = 'white';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.color = '#667eea';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.15)';
                          }}
                        >
                          <FaCheck className="me-2" />
                          Select Seats
                        </Button>
                        <p className="text-muted mb-0 small text-center">
                          Click to enable selection mode, then click seats to select them
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="d-flex align-items-center justify-content-between p-3" style={{
                          background: 'linear-gradient(135deg, #e6f0ff 0%, #d0e0ff 100%)',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 126, 234, 0.2)'
                        }}>
                          <span className="fw-medium text-primary">
                            {selectedSeatIds.size} seat{selectedSeatIds.size !== 1 ? 's' : ''} selected
                          </span>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={clearSelection}
                            className="fw-medium"
                          >
                            Clear
                          </Button>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            onClick={() => setShowBulkStatusModal(true)}
                            className="flex-fill fw-medium"
                            disabled={selectedSeatIds.size === 0}
                            style={{
                              height: '40px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              background: selectedSeatIds.size > 0 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#cbd5e0',
                              border: 'none',
                              borderRadius: '8px',
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: selectedSeatIds.size > 0 ? '0 2px 8px rgba(102, 126, 234, 0.25)' : 'none'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedSeatIds.size > 0) {
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.35)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedSeatIds.size > 0) {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.25)';
                              }
                            }}
                          >
                            Change Status ({selectedSeatIds.size})
                          </Button>
                          <Button
                            variant="success"
                            onClick={() => setShowBulkTypeModal(true)}
                            className="flex-fill fw-medium"
                            disabled={selectedSeatIds.size === 0}
                            style={{
                              height: '40px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              background: selectedSeatIds.size > 0 ? 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' : '#cbd5e0',
                              border: 'none',
                              borderRadius: '8px',
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: selectedSeatIds.size > 0 ? '0 2px 8px rgba(72, 187, 120, 0.25)' : 'none'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedSeatIds.size > 0) {
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(72, 187, 120, 0.35)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedSeatIds.size > 0) {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(72, 187, 120, 0.25)';
                              }
                            }}
                          >
                            Change Type ({selectedSeatIds.size})
                          </Button>
                          <Button
                            variant="danger"
                            onClick={handleBulkDeleteSeats}
                            className="fw-medium"
                            disabled={selectedSeatIds.size === 0 || saving}
                            style={{
                              height: '40px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              borderRadius: '8px',
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: selectedSeatIds.size > 0 ? '0 2px 8px rgba(220, 38, 38, 0.25)' : 'none',
                              minWidth: '80px'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedSeatIds.size > 0 && !saving) {
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.35)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedSeatIds.size > 0) {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.25)';
                              }
                            }}
                          >
                            <FaTrash className="me-1" />
                            Delete
                          </Button>
                        </div>
                        <p className="text-muted mb-0 small text-center">
                          Click seats on the map to select them, then change their type, status, or delete them
                        </p>
                        
                        {/* Quick Selection */}
                        <div className="mt-3 pt-3" style={{
                          borderTop: '1px solid rgba(0, 0, 0, 0.1)'
                        }}>
                          <Form.Label className="text-muted small fw-medium mb-2">Quick Selection</Form.Label>
                          <Form.Control
                            ref={quickSelectInputRef}
                            type="text"
                            placeholder="T01-01 to T01-10, A01 to A07, T25-01 to T25-06"
                            className="mb-2"
                            style={{
                              fontSize: '0.9rem',
                              borderRadius: '8px',
                              border: '2px solid #e2e8f0',
                              padding: '8px 12px'
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleQuickSelect();
                              }
                            }}
                          />
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleQuickSelect}
                              className="flex-fill fw-medium"
                              style={{
                                fontSize: '0.85rem',
                                borderRadius: '6px'
                              }}
                            >
                              Add Range
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                if (quickSelectInputRef.current) {
                                  quickSelectInputRef.current.value = '';
                                }
                              }}
                              className="fw-medium"
                              style={{
                                fontSize: '0.85rem',
                                borderRadius: '6px'
                              }}
                            >
                              Clear
                            </Button>
                          </div>
                          <p className="text-muted mb-0 small mt-2" style={{ fontSize: '0.75rem' }}>
                            Format: ROW-COL1 to ROW-COL2 (e.g., T01-01 to T01-10)
                            <br />
                            Multiple ranges: Separate with commas (e.g., T01-01 to T01-10, T25-01 to T25-06)
                          </p>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Save Button */}
                <Button
                  variant="primary"
                  onClick={Object.keys(seatSets).length > 0 ? saveMultiDaySeatSets : handleSaveSeatMap}
                  disabled={saving}
                  className="fw-medium"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                    }
                  }}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" /> Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" /> 
                      {Object.keys(seatSets).length > 0 ? 
                        `Save ${Object.keys(seatSets).length} Day Sets` : 
                        'Save Seat Map'
                      }
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Create Seat Map Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Modal.Title className="fw-bold">Create New Seat Map</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 24px rgba(79, 172, 254, 0.3)'
            }}>
              <FaMap style={{ color: 'white', fontSize: '24px' }} />
            </div>
            <h5 className="fw-bold mb-2">Configure Your Seat Map</h5>
            <p className="text-muted mb-0">Set the number of rows and columns for your seating arrangement</p>
          </div>
          
          <Alert variant="info" className="mb-4" style={{
            background: 'linear-gradient(135deg, #ebf8ff 0%, #bee3f8 100%)',
            border: '1px solid rgba(59, 130, 246, 0.1)',
            borderRadius: '12px'
          }}>
            <div className="d-flex align-items-center gap-2">
              <FaInfoCircle style={{ color: '#3182ce' }} />
              <strong>Seat Configuration:</strong>
            </div>
                        <p className="mb-0 mt-2">
                          Seats will be automatically named using the format: Row Letter + Column Number (e.g., A01, A02, B01, B02...)
                          <br />
                          Seat IDs will use the format: row-{`{section}`}{`{row}`}-{`{column}`} (e.g., row-a1-01, row-a1-02, row-b1-01...)
                          <br />
                          Seat types and pricing will be based on your event's pricing configuration.
                        </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateSeatMap}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            Create Map
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Seats Modal */}
      <Modal show={showAddSeatsModal} onHide={() => setShowAddSeatsModal(false)} size="lg">
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Modal.Title className="fw-bold">Add Seats on Top</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 24px rgba(72, 187, 120, 0.3)'
            }}>
              <FaPlus style={{ color: 'white', fontSize: '24px' }} />
            </div>
            <h5 className="fw-bold mb-2">Add More Seats</h5>
            <p className="text-muted mb-0">Add new rows and columns on top of your existing seat map</p>
          </div>
          
          <Alert variant="info" className="mb-4" style={{
            background: 'linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%)',
            border: '1px solid rgba(72, 187, 120, 0.1)',
            borderRadius: '12px'
          }}>
            <div className="d-flex align-items-center gap-2">
              <FaInfoCircle style={{ color: '#38a169' }} />
              <strong>How it works:</strong>
            </div>
            <p className="mb-0 mt-2">
              New seats will be added at the top of your seat map. All existing seats will be shifted down automatically.
              <br />
              You can choose custom naming for the new seats (e.g., G01, V01) or use the default naming.
              <br />
              Current seat map: {seatMap.length} seats ({seatConfig.rows} rows × {seatConfig.columns} columns)
            </p>
          </Alert>

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small fw-medium">Rows to Add</Form.Label>
                  <Form.Control
                    type="number"
                    ref={addRowsRef}
                    defaultValue="1"
                    min="1"
                    max={addSeatsNamingMode === 'custom' || seatConfig.namingMode === 'custom' ? "1000" : "26"}
                    style={{
                      height: '48px',
                      fontSize: '0.95rem',
                      fontWeight: '400',
                      color: '#2d3748',
                      backgroundColor: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}
                  />
                  <Form.Text className="text-muted small">
                    Number of new rows to add at the top
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small fw-medium">Columns to Add (Optional)</Form.Label>
                  <Form.Control
                    type="number"
                    ref={addColumnsRef}
                    defaultValue="0"
                    min="0"
                    max="100"
                    style={{
                      height: '48px',
                      fontSize: '0.95rem',
                      fontWeight: '400',
                      color: '#2d3748',
                      backgroundColor: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}
                  />
                  <Form.Text className="text-muted small">
                    Additional columns to add (0 = keep current width)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            {/* Naming Configuration for New Seats */}
            <Form.Group className="mb-3">
              <Form.Label className="text-muted small fw-medium">Naming Mode for New Seats</Form.Label>
              <Form.Select
                ref={addNamingModeRef}
                defaultValue={seatConfig.namingMode || 'default'}
                onChange={(e) => setAddSeatsNamingMode(e.target.value)}
                style={{
                  height: '48px',
                  fontSize: '0.95rem',
                  fontWeight: '400',
                  color: '#2d3748',
                  backgroundColor: '#f7fafc',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                }}
              >
                <option value="default">Default (A01, A02, B01, B02...)</option>
                <option value="custom">Custom (G01, G02, V01, V02...)</option>
              </Form.Select>
              <Form.Text className="text-muted small">
                Choose how new seats will be named (e.g., G01, V01, etc.)
              </Form.Text>
            </Form.Group>
            
            {(addSeatsNamingMode === 'custom' || seatConfig.namingMode === 'custom') && (
              <>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small fw-medium">Row Prefix</Form.Label>
                      <Form.Control
                        type="text"
                        ref={addRowPrefixRef}
                        defaultValue={seatConfig.rowPrefix || 'G'}
                        placeholder="e.g., G, V, G1, Section1"
                        style={{
                          height: '48px',
                          fontSize: '0.95rem',
                          fontWeight: '400',
                          color: '#2d3748',
                          backgroundColor: '#f7fafc',
                          border: '2px solid #e2e8f0',
                          borderRadius: '10px',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                        }}
                      />
                      <Form.Text className="text-muted small">
                        Prefix for seat names. Each different prefix starts numbering from 01.
                        <br />
                        Examples: "G" → G01, G02, G03... (continues across all rows) | "V" → V01, V02, V03... (starts fresh from 01) | "G1" → G1-01, G1-02... (if row number included)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small fw-medium">Options</Form.Label>
                      <div>
                        <Form.Check
                          type="checkbox"
                          ref={addUseRowNumberRef}
                          defaultChecked={seatConfig.useRowNumber !== false}
                          label="Include row number"
                          className="small"
                        />
                        <Form.Text className="text-muted small d-block mt-1">
                          If checked: G1-01, G1-02, G2-01... If unchecked: G01, G02... (same prefix for all rows)
                        </Form.Text>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            
            {/* Initial Status Selector for New Seats */}
            <Form.Group className="mb-3">
              <Form.Label className="text-muted small fw-medium">Initial Status for New Seats</Form.Label>
              <Form.Select
                ref={addInitialStatusRef}
                defaultValue="available"
                style={{
                  height: '48px',
                  fontSize: '0.95rem',
                  fontWeight: '400',
                  color: '#2d3748',
                  backgroundColor: '#f7fafc',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                }}
              >
                <option value="available">Available</option>
                <option value="pending">Pending</option>
                <option value="reserved">Reserved</option>
                <option value="disabled">Disabled</option>
              </Form.Select>
              <Form.Text className="text-muted small">
                All newly added seats will be created with this initial status
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Button variant="outline-secondary" onClick={() => setShowAddSeatsModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleAddSeats}
            style={{
              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            Add Seats
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Seat Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Modal.Title className="fw-bold">Edit Seat</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {editingSeat && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="text-muted small fw-medium">Seat Name</Form.Label>
                <Form.Control
                  type="text"
                  ref={seatNameRef}
                  value={editingSeat.name || ''}
                  onChange={(e) => setEditingSeat({...editingSeat, name: e.target.value})}
                  placeholder="e.g., T11-11"
                  style={{
                    height: '48px',
                    fontSize: '0.95rem',
                    fontWeight: '400',
                    color: '#2d3748',
                    backgroundColor: '#f7fafc',
                    border: '2px solid #e2e8f0',
                    borderRadius: '10px',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                  }}
                />
                <Form.Text className="text-muted small">
                  Change the seat name (e.g., from T11-04 to T11-11)
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label className="text-muted small fw-medium">Seat Type</Form.Label>
                <Form.Select
                  ref={seatTypeRef}
                  value={editingSeat.type}
                  onChange={(e) => setEditingSeat({...editingSeat, type: e.target.value})}
                  style={{
                    height: '48px',
                    fontSize: '0.95rem',
                    fontWeight: '400',
                    color: '#2d3748',
                    backgroundColor: '#f7fafc',
                    border: '2px solid #e2e8f0',
                    borderRadius: '10px',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                  }}
                >
                  {seatConfig.seatTypes.map((type, index) => (
                    <option key={index} value={type.id}>{type.name} - ${type.price}</option>
                  ))}
                  {seatConfig.seatTypes.length === 0 && (
                    <option value="default">Standard</option>
                  )}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label className="text-muted small fw-medium">Price</Form.Label>
                <Form.Control
                  type="number"
                  ref={seatPriceRef}
                  defaultValue={editingSeat.price}
                  onChange={(e) => setEditingSeat({...editingSeat, price: parseFloat(e.target.value) || 0})}
                  min="0"
                  step="0.01"
                  style={{
                    height: '48px',
                    fontSize: '0.95rem',
                    fontWeight: '400',
                    color: '#2d3748',
                    backgroundColor: '#f7fafc',
                    border: '2px solid #e2e8f0',
                    borderRadius: '10px',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                  }}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label className="text-muted small fw-medium">Status</Form.Label>
                <Form.Select
                  ref={seatStatusRef}
                  value={editingSeat.status}
                  onChange={(e) => setEditingSeat({...editingSeat, status: e.target.value})}
                  style={{
                    height: '48px',
                    fontSize: '0.95rem',
                    fontWeight: '400',
                    color: '#2d3748',
                    backgroundColor: '#f7fafc',
                    border: '2px solid #e2e8f0',
                    borderRadius: '10px',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                  }}
                >
                  <option value="available">Available</option>
                  <option value="reserved">Reserved</option>
                  <option value="sold">Booked</option>
                  <option value="disabled">Disabled</option>
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveSeat}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Assignment Modal */}
      <Modal show={showBulkAssignModal} onHide={() => setShowBulkAssignModal(false)}>
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Modal.Title className="fw-bold">Bulk Seat Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 24px rgba(237, 137, 54, 0.3)'
            }}>
              <FaUsers style={{ color: 'white', fontSize: '24px' }} />
            </div>
            <h5 className="fw-bold mb-2">Assign Seat Types by Range</h5>
            <p className="text-muted mb-0">Select a range of seats and assign them a specific type</p>
          </div>
          
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small fw-medium">Start Row</Form.Label>
                  <Form.Select
                    value={bulkAssignment.startRow}
                    onChange={(e) => setBulkAssignment({...bulkAssignment, startRow: parseInt(e.target.value)})}
                    style={{
                      height: '48px',
                      fontSize: '0.95rem',
                      fontWeight: '400',
                      color: '#2d3748',
                      backgroundColor: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    {Array.from({length: seatConfig.rows}, (_, i) => (
                      <option key={i} value={i}>{String.fromCharCode(65 + i)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small fw-medium">End Row</Form.Label>
                  <Form.Select
                    value={bulkAssignment.endRow}
                    onChange={(e) => setBulkAssignment({...bulkAssignment, endRow: parseInt(e.target.value)})}
                    style={{
                      height: '48px',
                      fontSize: '0.95rem',
                      fontWeight: '400',
                      color: '#2d3748',
                      backgroundColor: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    {Array.from({length: seatConfig.rows}, (_, i) => (
                      <option key={i} value={i}>{String.fromCharCode(65 + i)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small fw-medium">Start Column</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max={seatConfig.columns - 1}
                    value={bulkAssignment.startCol}
                    onChange={(e) => setBulkAssignment({...bulkAssignment, startCol: parseInt(e.target.value)})}
                    style={{
                      height: '48px',
                      fontSize: '0.95rem',
                      fontWeight: '400',
                      color: '#2d3748',
                      backgroundColor: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small fw-medium">End Column</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max={seatConfig.columns - 1}
                    value={bulkAssignment.endCol}
                    onChange={(e) => setBulkAssignment({...bulkAssignment, endCol: parseInt(e.target.value)})}
                    style={{
                      height: '48px',
                      fontSize: '0.95rem',
                      fontWeight: '400',
                      color: '#2d3748',
                      backgroundColor: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-muted small fw-medium">Seat Type</Form.Label>
              <Form.Select
                value={bulkAssignment.seatType}
                onChange={(e) => setBulkAssignment({...bulkAssignment, seatType: e.target.value})}
                style={{
                  height: '48px',
                  fontSize: '0.95rem',
                  fontWeight: '400',
                  color: '#2d3748',
                  backgroundColor: '#f7fafc',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                }}
              >
                {seatConfig.seatTypes.map((type, index) => (
                  <option key={index} value={type.id}>{type.name} - ${type.price}</option>
                ))}
                {seatConfig.seatTypes.length === 0 && (
                  <option value="default">Standard</option>
                )}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Button variant="outline-secondary" onClick={() => setShowBulkAssignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBulkAssignSeatType}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            Assign Seats
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Status Change Modal */}
      <Modal show={showBulkStatusModal} onHide={() => setShowBulkStatusModal(false)}>
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Modal.Title className="fw-bold">Change Status for Selected Seats</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="mb-3">
            <p className="text-muted mb-3">
              You have selected <strong>{selectedSeatIds.size}</strong> seat{selectedSeatIds.size !== 1 ? 's' : ''}. 
              Choose the new status to apply to all selected seats.
            </p>
          </div>
          
          <Form.Group className="mb-3">
            <Form.Label className="text-muted small fw-medium">New Status</Form.Label>
            <Form.Select
              ref={bulkStatusRef}
              defaultValue="available"
              style={{
                height: '48px',
                fontSize: '0.95rem',
                fontWeight: '400',
                color: '#2d3748',
                backgroundColor: '#f7fafc',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
              }}
            >
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="sold">Sold/Booked</option>
              <option value="disabled">Disabled</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Button variant="outline-secondary" onClick={() => setShowBulkStatusModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBulkStatusChange}
            disabled={saving || selectedSeatIds.size === 0}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            {saving ? 'Updating...' : `Update ${selectedSeatIds.size} Seat${selectedSeatIds.size !== 1 ? 's' : ''}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Seat Type Change Modal */}
      <Modal show={showBulkTypeModal} onHide={() => setShowBulkTypeModal(false)}>
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Modal.Title className="fw-bold">Change Type for Selected Seats</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="mb-3">
            <p className="text-muted mb-3">
              You have selected <strong>{selectedSeatIds.size}</strong> seat{selectedSeatIds.size !== 1 ? 's' : ''}.
              Choose the new seat type to apply to all selected seats.
            </p>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="text-muted small fw-medium">New Seat Type</Form.Label>
            <Form.Select
              ref={bulkSeatTypeRef}
              defaultValue={seatConfig.seatTypes[0]?.id || "default"}
              style={{
                height: '48px',
                fontSize: '0.95rem',
                fontWeight: '400',
                color: '#2d3748',
                backgroundColor: '#f7fafc',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
              }}
            >
              {seatConfig.seatTypes.map((type, index) => (
                <option key={index} value={type.id}>{type.name} - ${type.price}</option>
              ))}
              {seatConfig.seatTypes.length === 0 && (
                <option value="default">Standard</option>
              )}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Button variant="outline-secondary" onClick={() => setShowBulkTypeModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleBulkTypeChange}
            disabled={saving || selectedSeatIds.size === 0}
            style={{
              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            {saving ? 'Updating...' : `Update ${selectedSeatIds.size} Seat${selectedSeatIds.size !== 1 ? 's' : ''}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Shared Seating Modal */}
      <Modal show={showSharedSeatingModal} onHide={() => setShowSharedSeatingModal(false)} size="lg">
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Modal.Title className="fw-bold">Copy Seat Layout from Another Event</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 24px rgba(72, 187, 120, 0.3)'
            }}>
              <FaUsers style={{ color: 'white', fontSize: '24px' }} />
            </div>
            <h5 className="fw-bold mb-2">Shared Seating Configuration</h5>
            <p className="text-muted mb-0">Copy seat layout from another event for multi-day events</p>
          </div>
          
          <Alert variant="info" className="mb-4" style={{
            background: 'linear-gradient(135deg, #ebf8ff 0%, #bee3f8 100%)',
            border: '1px solid rgba(59, 130, 246, 0.1)',
            borderRadius: '12px'
          }}>
            <div className="d-flex align-items-center gap-2">
              <FaInfoCircle style={{ color: '#3182ce' }} />
              <strong>Perfect for Multi-Day Events:</strong>
            </div>
            <p className="mb-0 mt-2">
              Use this feature when you have multiple events (e.g., Aug 2 and Aug 3) that share the same physical seating arrangement but need different ticket IDs.
            </p>
          </Alert>
          
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="text-muted small fw-medium">Source Event</Form.Label>
              <Form.Select
                value={sharedSeatingConfig.sourceEventId}
                onChange={(e) => setSharedSeatingConfig({...sharedSeatingConfig, sourceEventId: e.target.value})}
                style={{
                  height: '48px',
                  fontSize: '0.95rem',
                  fontWeight: '400',
                  color: '#2d3748',
                  backgroundColor: '#f7fafc',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                }}
              >
                <option value="">Select an event to copy from...</option>
                {availableEvents.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title} ({event.id})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <div className="card border-0 mb-4" style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '12px',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}>
              <div className="card-body p-3">
                <h6 className="fw-semibold mb-3">Copy Options</h6>
                
                <Form.Check
                  type="checkbox"
                  id="copySeatNames"
                  label="Copy seat names (A01, A02, etc.)"
                  checked={sharedSeatingConfig.copySeatNames}
                  onChange={(e) => setSharedSeatingConfig({...sharedSeatingConfig, copySeatNames: e.target.checked})}
                  className="mb-2"
                />
                
                <Form.Check
                  type="checkbox"
                  id="generateNewIds"
                  label="Generate new seat IDs (recommended for multi-day events)"
                  checked={sharedSeatingConfig.generateNewIds}
                  onChange={(e) => setSharedSeatingConfig({...sharedSeatingConfig, generateNewIds: e.target.checked})}
                  className="mb-2"
                />
                
                <Form.Check
                  type="checkbox"
                  id="copySeatTypes"
                  label="Copy seat types and pricing"
                  checked={sharedSeatingConfig.copySeatTypes}
                  onChange={(e) => setSharedSeatingConfig({...sharedSeatingConfig, copySeatTypes: e.target.checked})}
                  className="mb-2"
                />
                
                <Form.Check
                  type="checkbox"
                  id="copySeatStatus"
                  label="Copy seat status (available/reserved/booked)"
                  checked={sharedSeatingConfig.copySeatStatus}
                  onChange={(e) => setSharedSeatingConfig({...sharedSeatingConfig, copySeatStatus: e.target.checked})}
                  className="mb-0"
                />
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Button variant="outline-secondary" onClick={() => setShowSharedSeatingModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleCopySeatLayout}
            disabled={!sharedSeatingConfig.sourceEventId}
            style={{
              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            Copy Seat Layout
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Multi-Date Seat Map Modal */}
      <Modal show={showMultiDayModal} onHide={() => setShowMultiDayModal(false)}>
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Modal.Title className="fw-bold text-dark">
            <FaCalendarAlt className="me-2" />
            Create Multi-Day Seat Sets
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          padding: '2rem'
        }}>
          <div className="mb-4">
            <h6 className="fw-semibold mb-3 text-dark">Select Day for Seat Set</h6>
            <Form.Group>
              <Form.Select
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                style={{
                  height: '50px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  fontWeight: '500',
                  background: 'white',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
              >
                <option value="">Choose a day...</option>
                <option value="aug2">Aug 2 - Premium Admission</option>
                <option value="aug3">Aug 3 - General Admission</option>
              </Form.Select>
            </Form.Group>
          </div>
          
          {Object.keys(seatSets).length > 0 && (
            <div className="card border-0 mb-4" style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
            }}>
              <div className="card-body p-4">
                <h6 className="fw-semibold mb-3">Existing Day Sets</h6>
                <div className="space-y-2">
                  {Object.keys(seatSets).map(day => (
                    <div key={day} className="d-flex justify-content-between align-items-center p-3" style={{
                      background: 'white',
                      borderRadius: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.05)'
                    }}>
                      <div>
                        <span className="fw-medium text-dark">
                          {day === "aug2" ? "Aug 2 - Premium Admission" : "Aug 3 - General Admission"}
                        </span>
                        <br />
                        <small className="text-muted">
                          {seatSets[day].length} seats • {seatConfig.rows} rows × {seatConfig.columns} columns
                        </small>
                      </div>
                      <Badge bg="success" className="fw-medium">
                        {seatSets[day].length} seats
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <div className="alert alert-info border-0" style={{
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            borderRadius: '12px',
            border: '1px solid #93c5fd'
          }}>
            <div className="d-flex align-items-start">
              <FaInfoCircle className="me-2 mt-1 text-primary" />
              <div>
                <strong>Multi-Day Seat Sets</strong>
                <br />
                This will create separate seat sets for Aug 2 and Aug 3 with the same layout but different seat IDs. 
                Each set will be associated with different ticket types (Premium vs General Admission).
              </div>
            </div>
          </div>
        </Modal.Body>
        
        <Modal.Footer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Button variant="outline-secondary" onClick={() => setShowMultiDayModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={createBothDaySets}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: '600',
              boxShadow: '0 4px 6px rgba(16, 185, 129, 0.25)'
            }}
          >
            <FaPlus className="me-2" />
            Create Both Days
          </Button>
          <Button 
            variant="primary" 
            onClick={createMultiDaySeatSets}
            disabled={!selectedDay}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: '600',
              boxShadow: '0 4px 6px rgba(102, 126, 234, 0.25)'
            }}
          >
            <FaPlus className="me-2" />
            Create Selected Day
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Debug Information */}
      {debugInfo && (
        <div className="card border-0 mb-4" style={{
          background: 'linear-gradient(135deg, #fef5e7 0%, #fed7aa 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
        }}>
          <div className="card-body p-4">
            <h6 className="fw-semibold mb-3 text-dark">
              <FaBug className="me-2" />
              Debug Information
            </h6>
            <pre style={{
              fontSize: '12px',
              background: 'white',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              maxHeight: '200px',
              overflow: 'auto'
            }}>
              {debugInfo}
            </pre>
          </div>
        </div>
      )}

      {/* Seat Map Configuration */}
    </>
  );
} 
